/**
 * LDAP Service for Amazon User Email Lookup
 * Frontend-only implementation with permanent localStorage caching
 */

// Type definitions
interface LDAPUserInfo {
  userAlias: string;
  email: string;
  cachedAt: number;
}

interface LDAPServiceConfig {
  cachePrefix: string;
  emailDomain: string;
  fallbackDomain: string;
}

interface LDAPResult {
  success: boolean;
  email?: string;
  error?: string;
}

interface CacheEntry {
  email: string;
  timestamp: number;
}

/**
 * LDAP Service Class
 * Handles email lookup for Amazon users with permanent caching
 */
class LDAPService {
  private static instance: LDAPService;
  private config: LDAPServiceConfig;

  private constructor() {
    this.config = {
      cachePrefix: 'ldap_email_',
      emailDomain: 'amazon.com',
      fallbackDomain: 'amazon.com'
    };
  }

  /**
   * Singleton pattern to ensure single instance
   */
  public static getInstance(): LDAPService {
    if (!LDAPService.instance) {
      LDAPService.instance = new LDAPService();
    }
    return LDAPService.instance;
  }

  /**
   * Get cache key for a user alias
   */
  private getCacheKey(userAlias: string): string {
    return `${this.config.cachePrefix}${userAlias.toLowerCase()}`;
  }

  /**
   * Check if localStorage is available
   */
  private isLocalStorageAvailable(): boolean {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get email from permanent cache
   */
  private getCachedEmail(userAlias: string): string | null {
    if (!this.isLocalStorageAvailable()) {
      console.warn('🚫 LDAP SERVICE: localStorage not available, skipping cache');
      return null;
    }

    try {
      const cacheKey = this.getCacheKey(userAlias);
      const cachedData = localStorage.getItem(cacheKey);
      
      if (cachedData) {
        const parsed: CacheEntry = JSON.parse(cachedData);
        
        return parsed.email;
      }
    } catch (error) {
      console.warn('⚠️ LDAP CACHE ERROR: Failed to read from cache:', error);
    }

    return null;
  }

  /**
   * Store email in permanent cache
   */
  private setCachedEmail(userAlias: string, email: string): void {
    if (!this.isLocalStorageAvailable()) {
      console.warn('🚫 LDAP SERVICE: localStorage not available, skipping cache write');
      return;
    }

    try {
      const cacheKey = this.getCacheKey(userAlias);
      const cacheEntry: CacheEntry = {
        email,
        timestamp: Date.now()
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
     
    } catch (error) {
      console.warn('⚠️ LDAP CACHE ERROR: Failed to write to cache:', error);
    }
  }

  /**
   * Generate email address based on Amazon patterns
   */
  private generateEmail(userAlias: string): string {
    // Clean the user alias
    const cleanAlias = userAlias.toLowerCase().trim();
    
    // Amazon standard pattern: alias@amazon.com
    const email = `${cleanAlias}@${this.config.emailDomain}`;
    
    
    return email;
  }

  /**
   * Validate email format
   */
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Main method to get user email
   * Checks cache first, then generates and caches email
   */
  public async getUserEmail(userAlias: string): Promise<LDAPResult> {
   

    // Validate input
    if (!userAlias || typeof userAlias !== 'string' || userAlias.trim().length === 0) {
      const error = 'Invalid user alias provided';
      console.error('❌ LDAP ERROR:', error);
      return { success: false, error };
    }

    const cleanAlias = userAlias.trim();

    try {
      // Check permanent cache first
      const cachedEmail = this.getCachedEmail(cleanAlias);
      if (cachedEmail) {
        return { success: true, email: cachedEmail };
      }

      // Generate email (simulating LDAP lookup)
       
      const generatedEmail = this.generateEmail(cleanAlias);

      // Validate generated email
      if (!this.isValidEmail(generatedEmail)) {
        const error = `Generated invalid email format: ${generatedEmail}`;
        console.error('❌ LDAP ERROR:', error);
        return { success: false, error };
      }

      // Cache the result permanently
      this.setCachedEmail(cleanAlias, generatedEmail);

      
      return { success: true, email: generatedEmail };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('❌ LDAP SERVICE ERROR:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Clear cache for a specific user (utility method)
   */
  public clearUserCache(userAlias: string): boolean {
    if (!this.isLocalStorageAvailable()) {
      return false;
    }

    try {
      const cacheKey = this.getCacheKey(userAlias);
      localStorage.removeItem(cacheKey);
      
      return true;
    } catch (error) {
      console.warn('⚠️ LDAP CACHE ERROR: Failed to clear cache:', error);
      return false;
    }
  }

  /**
   * Clear all LDAP cache entries (utility method)
   */
  public clearAllCache(): number {
    if (!this.isLocalStorageAvailable()) {
      return 0;
    }

    let clearedCount = 0;
    const keysToRemove: string[] = [];

    try {
      // Find all LDAP cache keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.config.cachePrefix)) {
          keysToRemove.push(key);
        }
      }

      // Remove all found keys
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        clearedCount++;
      });

     
      return clearedCount;
    } catch (error) {
      console.warn('⚠️ LDAP CACHE ERROR: Failed to clear all cache:', error);
      return clearedCount;
    }
  }

  /**
   * Get cache statistics (utility method)
   */
  public getCacheStats(): { totalEntries: number; oldestEntry?: number; newestEntry?: number } {
    if (!this.isLocalStorageAvailable()) {
      return { totalEntries: 0 };
    }

    let totalEntries = 0;
    let oldestEntry: number | undefined;
    let newestEntry: number | undefined;

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.config.cachePrefix)) {
          totalEntries++;
          
          try {
            const data = localStorage.getItem(key);
            if (data) {
              const parsed: CacheEntry = JSON.parse(data);
              if (!oldestEntry || parsed.timestamp < oldestEntry) {
                oldestEntry = parsed.timestamp;
              }
              if (!newestEntry || parsed.timestamp > newestEntry) {
                newestEntry = parsed.timestamp;
              }
            }
          } catch {
            // Skip invalid entries
          }
        }
      }
    } catch (error) {
      console.warn('⚠️ LDAP CACHE ERROR: Failed to get cache stats:', error);
    }

    return { totalEntries, oldestEntry, newestEntry };
  }
}

// Export singleton instance
export const ldapService = LDAPService.getInstance();

// Export types for use in other files
export type { LDAPResult, LDAPUserInfo, LDAPServiceConfig };

// Default export
export default ldapService;
